import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { regularApiClient } from "../utils/kiota-client";
import type { CoolingRegimeProductGroupDto } from "../../../RoyalFloraHolland.Aap.Services.Api.Web.Kiota/Typescript/Api/models/index";
import { fetchAllCoolingRegimeTemperatures } from "../services/coolingRegimeTemperatureService";
import { fetchAllCoolingRegimePriorities } from "../services/coolingRegimePriorityService";
import { deleteCoolingRegimeProductGroup, createCoolingRegimeProductGroup, type CreateCoolingRegimeProductGroupPayload, updateCoolingRegimeProductGroupPriorities } from "../services/coolingRegimeProductGroupService";
import { updateCoolingRegimeProductGroupTemperatures } from "../services/coolingRegimeProductGroupService";

/**
 * Hook to fetch a single cooling regime product group by ID
 */
export function useCoolingRegimeProductGroup(
  productGroupCode: string | undefined,
) {
  return useQuery({
    queryKey: ["coolingRegimeProductGroup", productGroupCode],
    queryFn: async (): Promise<CoolingRegimeProductGroupDto | null> => {
      if (!productGroupCode) {
        throw new Error("product group Code is required");
      }

      try {
        const data = await regularApiClient.api
          .vWithVersion("1")
          .coolingRegimeProductGroup.byCode
          .byCode(parseInt(productGroupCode))
          .get({
            queryParameters: {
              includeAll: true,
            },
          });
        return data as CoolingRegimeProductGroupDto;
      } catch (err: any) {
        const status = err?.statusCode ?? err?.responseStatusCode ?? err?.code;
        const msg = typeof err?.message === "string" ? err.message : "";
        // Treat 404/not found as "no data" rather than an error
        if (status === 404 || /not\s*found/i.test(msg)) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!productGroupCode,
  });
}

/**
 * Hook to fetch all cooling regime temperatures for dropdown options
 */
export function useCoolingRegimeTemperatureOptions() {
  return useQuery({
    queryKey: ["coolingRegimeTemperatureOptions"],
    queryFn: fetchAllCoolingRegimeTemperatures,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}

/**
 * Hook to fetch all cooling regime priorities for dropdown options
 */
export function useCoolingRegimePriorityOptions() {
  return useQuery({
    queryKey: ["coolingRegimePriorityOptions"],
    queryFn: fetchAllCoolingRegimePriorities,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}

/**
 * Hook to update cooling regime productgroup temperatures
 */
export function useUpdateCoolingRegimeProductGroupTemperatures() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCoolingRegimeProductGroupTemperatures,
    onSuccess: (data) => {
      // Invalidate and refetch cooling regime product group details
      queryClient.invalidateQueries({
        queryKey: ["coolingRegimeProductGroup", data.productGroupCode?.toString()],
      });
    },
  });
}

/**
 * Hook to update cooling regime product group priorities
 */
export function useUpdateCoolingRegimeProductGroupPriorities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCoolingRegimeProductGroupPriorities,
    onSuccess: (data) => {
      // Invalidate and refetch cooling regime product group details
      queryClient.invalidateQueries({
        queryKey: ["coolingRegimeProductGroup", data.productGroupCode?.toString()],
      });
    },
  });
}

/**
 * Hook to delete cooling regime product group
 */
export function useDeleteCoolingRegimeProductGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCoolingRegimeProductGroup,
    onSuccess: () => {
      // Invalidate any cached coolingRegimeProductGroup queries
      queryClient.invalidateQueries({ queryKey: ["coolingRegimeProductGroup"] });
    },
  });
}

/**
 * Hook to create cooling regime product group
 */
export function useCreateCoolingRegimeProductGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateCoolingRegimeProductGroupPayload) =>
      createCoolingRegimeProductGroup(payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["coolingRegimeProductGroup", data.productGroupCode?.toString()],
      });
    },
  });
}
